import {
  Flex,
  ChartSkeleton,
  ASSET_SYMBOLS_PALETTE,
  ProgressBar,
  DoughnutChart,
  CurrencyValue,
  Card,
  useIsMobile,
  formatPercentage,
  hexToRgb
} from '@blockanalitica/ui';
import { useMemo } from 'react';
import { useApi } from '@/api';
import { useChartTooltip } from '@/hooks';
import _ from 'lodash';

type BreakdownResponse = {
  symbol: string;
  percent: string | number;
  amount_usd: string | null;
};

export default function CollateralAssetBreakdown({
  marketAddress
}: {
  marketAddress: string;
}) {
  const isMobile = useIsMobile();
  const [data, error] = useApi<BreakdownResponse[]>({
    url: `/markets/${marketAddress}/collateral/breakdown/`
  });

  const parsedData = useMemo(() => {
    if (!data) {
      return;
    }

    const ordered = _.orderBy(data, [(item) => Number(item.percent)], ['desc']);

    return ordered.map((row) => ({
      text: row.symbol,
      additionalValue: (
        <CurrencyValue value={row.amount_usd} options={{ currency: 'USD' }} />
      ),
      percent: row.percent,
      color:
        ASSET_SYMBOLS_PALETTE[row.symbol.replace('-emode', '')] || '#ffffff'
    }));
  }, [data]);

  const customTooltip = useChartTooltip(
    parsedData?.map(({ text, percent, additionalValue }) => ({
      text,
      percent,
      additionalValue
    })),
    'text',
    'percent',
    'additionalValue'
  );

  const chartData = useMemo(() => {
    if (!parsedData) {
      return;
    }

    const labels: string[] = [];
    const backgroundColor: string[] = [];
    const colorsBorder: string[] = [];
    const datasetData: number[] = [];
    parsedData.forEach((row) => {
      const { r, g, b } = hexToRgb(row.color);
      labels.push(_.capitalize(row.text));
      backgroundColor.push(`rgba(${r}, ${g}, ${b}, 0.5)`);
      colorsBorder.push(`rgba(${r}, ${g}, ${b}, 1)`);
      datasetData.push(Number(row.percent));
    });

    return {
      labels,
      backgroundColor,
      colorsBorder,
      datasets: [
        {
          data: datasetData,
          hoverOffset: 15
        }
      ]
    };
  }, [parsedData]);
  const customConfig = useMemo(
    () => ({
      options: {
        layout: {
          padding: 10
        },
        plugins: {
          legend: {
            position: 'bottom'
          },
          tooltip: {
            enabled: false,
            position: 'nearest' as const,
            external: customTooltip
          }
        }
      }
    }),
    [customTooltip]
  );

  if (error) {
    throw error;
  }

  if (!chartData) {
    return (
      <Flex flexDirection="column" gap="xs">
        <ChartSkeleton />
      </Flex>
    );
  }

  return (
    <Card flexDirection="row" gap="s">
      <Flex flexDirection="column" gap="s" flex="1">
        {parsedData?.map(({ text, percent, additionalValue, color }) => (
          <ProgressBar
            key={text}
            text={text}
            percent={percent}
            additionalValue={additionalValue}
            color={color}
            flex="0"
          />
        ))}
      </Flex>

      {!isMobile ? (
        <Card flexDirection="column" gap="m" variant="dark">
          <Flex>
            <DoughnutChart
              config={customConfig}
              data={chartData}
              valueFormatter={formatPercentage}
            />
          </Flex>
        </Card>
      ) : null}
    </Card>
  );
}
